import { noticeError } from '@ratehub/base-ui';

import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import fetchCreditCard from '../functions/fetchCreditCard';
import verifyCardIntegrity from '../functions/verifyCardIntegrity';
import getMonthlySpendingFromAverage from '../functions/getMonthlySpendingFromAverage';
import createCardInfoFromAPIResponse from './createCardInfoFromAPIResponse';


/**
 * Returns a 'cardInfo' CreditCardShape given a slug or id that is
 * expected to pass verifyCardIntegrity
 *
 * @param {Object} params
 * @param {string} [params.slug] the target card’s slug
 * @param {number} [params.id] the target card’s id (only one of slug or id is required)
 * @param {string} [params.productType] product type to verify against
 * @param {string} [params.languageCode] en | fr
 * @param {string} [params.province] the province code (e.g. ON)
 * @param {Object} [params.cardFinderUserAmounts=DEFAULT_CARD_PREFERENCES]  contextual user information, typically as provided to CardFinder wizard
 * @param {boolean} [params.isDraftMode] returns the latest draft version of the card
 * @returns {Promise<CreditCardShape|null>} CreditCardShape structure when successful, including verification
 */
async function fetchCardInfo({
    slug,
    id,
    productType,
    languageCode,
    province,
    cardFinderUserAmounts = DEFAULT_CARD_PREFERENCES,
    isDraftMode,
}) {
    const apiCard = await fetchCreditCard({
        slug,
        id,
        language: languageCode,
        province,
        monthlySpending: cardFinderUserAmounts.monthlySpending
            ?? getMonthlySpendingFromAverage(cardFinderUserAmounts.monthlySpendingAverage),
        isDraftMode,
    });

    if (apiCard && apiCard?.ids) {
        const cardInfo = createCardInfoFromAPIResponse(apiCard, cardFinderUserAmounts);
        const isValidCard = verifyCardIntegrity(cardInfo, productType || cardInfo.productType);

        if (isValidCard) {
            return cardInfo;
        } else {
            noticeError(
                new Error(
                    `[fetchCardInfo] Failed card validity check for “${apiCard.name}” [cardId: ${id}, productType: ${cardInfo?.productType || productType}]`,
                ),
                {
                    cardId: id,
                    cardResponse: apiCard,
                    slug,
                    productType,
                    languageCode,
                    isDraftMode,
                },
            );

            return null;
        }
    }

    noticeError(
        new Error(`[fetchCardInfo] Failed to create card for “${apiCard.name}” [cardId: ${id}, productType: ${productType}]`),
        {
            cardId: id,
            cardResponse: apiCard,
            slug,
            productType,
            languageCode,
            isDraftMode,
        },
    );

    return null;
}


export default fetchCardInfo;
